import styled from '@emotion/styled';
import { TextArea } from '@vromo/ui';
import ConditionalTooltip from '@vromo/ui/components/ConditionalTooltip';
import Flex from '@vromo/ui/components/Flex';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Input from '@vromo/ui/components/Input';
import theme from '@vromo/ui/components/theme';
import Tooltip from '@vromo/ui/components/Tooltip';
import { darken } from 'polished';
import React, { ChangeEvent, FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAcceptedFleetNames, getCoveredZipCodesForRestaurant } from '../../FleetHelper';
import {
    AcceptedFleetOptions,
    FleetCoverageDetails,
    FleetOption,
    RestaurantType
} from '../../interfaces';
import FleetOptionListItem from './components/FleetOptionListItem';
import styles from './restaurantResult.style';

const Action = styled.a`
    font-size: 18px;
    margin-left: 8px;
    color: ${({ color }: any) => color};
    cursor: pointer;

    &:hover {
        color: ${({ color }: any) => darken(0.05, color)};
    }
`;

interface Props {
    entryNumber: number;
    restaurant: RestaurantType;
    showDetails: (detailsItem: FleetCoverageDetails) => void;
    updateAcceptedFleetOptions: (id: string, acceptedFleetOption: AcceptedFleetOptions) => void;
    acceptedFleetOptions: AcceptedFleetOptions[];
    onChange: (restaurant: RestaurantType) => void;
    editMode?: boolean;
    onToggleEditClick: (id: string) => void;
    onRemoveClick: (id: string) => void;
    canRemove?: boolean;
}

const RestaurantResult: FC<Props> = ({
    entryNumber,
    restaurant,
    showDetails,
    updateAcceptedFleetOptions,
    acceptedFleetOptions,
    onChange,
    editMode,
    onToggleEditClick,
    onRemoveClick,
    canRemove
}) => {
    const { t } = useTranslation();
    const { id, name, zipCodes, fleetOptions } = restaurant;

    const onChangeZipCodes = (e: ChangeEvent<HTMLTextAreaElement>) =>
        onChange({ id, name, fleetOptions, zipCodes: e.target.value });

    const onChangeName = (e: ChangeEvent<HTMLInputElement>) =>
        onChange({ id, name: e.target.value, zipCodes, fleetOptions });

    if (editMode) {
        return (
            <div style={styles.editContainer}>
                <div style={styles.removeIconContainer}>
                    {canRemove && (
                        <div style={styles.iconWrapper}>
                            <FontAwesome
                                onClick={() => onRemoveClick(id)}
                                icon="times"
                                large
                                style={styles.removeIcon}
                            />
                        </div>
                    )}
                </div>
                <div style={styles.inputsContainer}>
                    <h3 style={styles.title}>
                        {t('findFleets.restaurantNumber')}
                        {entryNumber + 1}
                    </h3>
                    <Input
                        style={styles.nameInput}
                        value={name}
                        onChange={onChangeName}
                        disabled={!editMode}
                        placeholder={t('findFleets.restaurantNamePlaceholder')}
                    />
                </div>
                <TextArea
                    style={styles.zipCodeInput}
                    disabled={!editMode}
                    placeholder={t('findFleets.zipCodeInputPlaceholder')}
                    value={zipCodes}
                    lockSize={true}
                    onChange={onChangeZipCodes}
                />
            </div>
        );
    }

    const acceptedFleetOptionNames = getAcceptedFleetNames(restaurant.id, acceptedFleetOptions);
    const coveredZipCodes = getCoveredZipCodesForRestaurant(restaurant, acceptedFleetOptions);

    const zipCodeArray = zipCodes.split(', ');

    const percentageOfCoverage = Math.round(
        Math.min((coveredZipCodes.length / zipCodeArray.length) * 100, 100)
    );

    const onToggleCoverageOption = (dspName: string) =>
        updateAcceptedFleetOptions(restaurant.id, {
            restaurantId: restaurant.id,
            acceptedFleetNames: acceptedFleetOptionNames.includes(dspName)
                ? acceptedFleetOptionNames.filter((name: string) => dspName !== name)
                : [...acceptedFleetOptionNames, dspName]
        });

    return (
        <div style={styles.container}>
            <Flex horizontal="left" vertical="middle">
                <h3 style={styles.resultTitle}>
                    {t('findFleets.restaurantNumber')}
                    {entryNumber + 1}
                </h3>
                <p style={styles.restaurantName}>{name}</p>
                <Flex style={styles.editButtonContainer} horizontal="right" vertical="middle">
                    <Tooltip label="Edit">
                        <Action
                            color={theme.text.color(theme.background)}
                            onClick={() => onToggleEditClick(id)}>
                            <FontAwesome icon="pencil" />
                        </Action>
                    </Tooltip>
                </Flex>
            </Flex>
            <Flex horizontal="left" vertical="middle">
                <span style={styles.zipCodeList}>
                    {zipCodeArray.map((zipCode: string, index: number) => {
                        const isLast = index === zipCodeArray.length - 1;
                        const isCovered = coveredZipCodes.includes(zipCode);
                        const color = !isCovered ? 'red' : theme.text.dark;
                        const fontWeight = !isCovered ? 'bold' : 'normal';
                        return (
                            <ConditionalTooltip label={isCovered ? '' : t('findFleets.noCoverage')}>
                                <span key={`${restaurant.name}-zipcode-item-${index}`}>
                                    <span style={{ color, fontWeight }}>{zipCode}</span>
                                    {!isLast && <span>, </span>}
                                </span>
                            </ConditionalTooltip>
                        );
                    })}
                </span>
            </Flex>
            <h3 style={styles.percentageTitle}>{`${percentageOfCoverage}${t(
                'findFleets.coverageTitle'
            )}`}</h3>
            {fleetOptions.map((fleetOption: FleetOption, index: number) => {
                const uncoveredZipCodeArray = zipCodeArray.filter(
                    (zipCode: string) => !fleetOption.zipCodeCoverage.includes(zipCode)
                );
                const isChecked = acceptedFleetOptionNames.includes(fleetOption.name);
                const onDetailsClicked = () =>
                    showDetails({
                        name: fleetOption.name,
                        coveredZipCodeArray: fleetOption.zipCodeCoverage,
                        uncoveredZipCodeArray
                    });
                const coveragePercentageLabel = `${fleetOption.zipCodeCoverage.length} ${t(
                    'findFleets.coverageOutOf'
                )} ${zipCodeArray.length}`;

                return (
                    <FleetOptionListItem
                        key={`${restaurant.name}-fleet-option-${index}`}
                        fleetName={fleetOption.name}
                        {...{
                            onToggleCoverageOption,
                            isChecked,
                            onDetailsClicked,
                            coveragePercentageLabel
                        }}
                    />
                );
            })}
        </div>
    );
};

export default memo(RestaurantResult);
